export {};

// ── Page-load loader overlay ─────────────────────────────────────────────────
// Self-contained bundle entry. Injects a full-page overlay into document.body,
// plays a cloud particle animation from sparse → full, then fades out.
// No mount point needed in Webflow — add <script type="module" src="loader.js">
// to page Head custom code, before the hero-particles script.

// ── Config ────────────────────────────────────────────────────────────────────
const FORM_MS          = 4500;  // sparse → full
const HOLD_MS          = 600;   // pause at 100% before fade
const BG_FADE_MS       = 1500;  // dark background fades out (reveals hero beneath)
const PARTICLE_FADE_MS = 4000;  // particle canvas lingers, then slowly dissolves
const COUNT         = 1000;
const CLOUD_R       = 1.18 * 1.05; // matches hero: latticeHalfExtent * 1.05
const CLOUD_SEED    = 0x9e3779b9;  // matches hero seed
const PARTICLE_SIZE = 1.8;
const FOV           = 580;
const V_BIAS        = 0.033; // downward vertical bias — matches hero

// ── Device-aware rotation — mirrors hero main.ts behaviour per device ─────────
// Desktop (interactive mode): Y=0.34 + X=0.22 dual-axis tumble
// Mobile  (loop mode):        Y=0.31, X=0    Y-only spin
const hasFinePointer = window.matchMedia('(pointer: fine)').matches;
// Mutable so the ?debug=1 panel can update them live
let rotSpeedY  = hasFinePointer ? 0.340 : 0.31;
let rotSpeedX  = hasFinePointer ? 0.220 : 0.00;
let rotSpeedZ  = 0.000;
let rotOffsetX = 0;
let rotOffsetZ = 0;

// ── Mulberry32 RNG — exact match to hero ─────────────────────────────────────
function mulberry32(seed: number): () => number {
  return () => {
    let t = (seed += 0x6d2b79f5);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// ── Cloud positions — exact match to hero ─────────────────────────────────────
function createCloudPositions(count: number, r: number, seed: number): Float32Array {
  const rng = mulberry32(seed);
  const out = new Float32Array(count * 3);
  for (let i = 0; i < count * 3; i += 3) {
    const u     = rng();
    const v     = rng();
    const theta = 2 * Math.PI * u;
    const phi   = Math.acos(2 * v - 1);
    const rad   = r * Math.cbrt(rng());
    const sinPhi = Math.sin(phi);
    out[i]     = rad * sinPhi * Math.cos(theta);
    out[i + 1] = rad * sinPhi * Math.sin(theta);
    out[i + 2] = rad * Math.cos(phi);
  }
  return out;
}

function easeInOut(t: number): number {
  return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
}

function easeOut(t: number): number {
  return 1 - Math.pow(1 - t, 3);
}

function init(): void {
  (window as any).__loaderActive = true;

  // ── Generate data ───────────────────────────────────────────────────────────
  const positions = createCloudPositions(COUNT, CLOUD_R, CLOUD_SEED);

  // Shuffled appearance order — particles materialise randomly across the cloud
  const appearOrder = new Uint16Array(COUNT);
  for (let i = 0; i < COUNT; i++) appearOrder[i] = i;
  const shuffleRng = mulberry32(0xface1234);
  for (let i = COUNT - 1; i > 0; i--) {
    const j = Math.floor(shuffleRng() * (i + 1));
    const tmp = appearOrder[i]!; appearOrder[i] = appearOrder[j]!; appearOrder[j] = tmp;
  }

  // ── Build overlay DOM ───────────────────────────────────────────────────────
  const overlay = document.createElement('div');
  overlay.style.cssText = [
    'position:fixed', 'inset:0', 'z-index:10000',
    'display:flex', 'flex-direction:column',
  ].join(';');

  // Background layer — fades out first to reveal hero beneath particles
  const bgDiv = document.createElement('div');
  bgDiv.style.cssText = 'position:absolute;inset:0;z-index:0;background:#080808;';
  overlay.appendChild(bgDiv);

  const canvas = document.createElement('canvas');
  canvas.style.cssText = 'flex:1;width:100%;display:block;position:relative;z-index:1;';
  overlay.appendChild(canvas);

  // Progress bar footer — fades with background
  const footer = document.createElement('div');
  footer.style.cssText = 'position:absolute;bottom:0;left:0;right:0;z-index:2;';

  const track = document.createElement('div');
  track.style.cssText = 'width:100%;height:3px;background:rgba(255,255,255,0.1);position:relative;';

  const fill = document.createElement('div');
  fill.style.cssText = 'position:absolute;top:0;left:0;height:100%;background:#fcfcfc;width:0%;';

  const label = document.createElement('div');
  label.style.cssText = [
    'position:absolute', 'bottom:calc(3px + 10px)',
    'font-family:"Public Sans",sans-serif', 'font-size:10px',
    'font-weight:300', 'letter-spacing:0.18em',
    'color:#fcfcfc', 'text-transform:uppercase',
    'white-space:nowrap', 'transform:translateX(-100%)', 'padding-right:12px',
  ].join(';');
  label.textContent = 'LOADING 0%';

  track.appendChild(fill);
  track.appendChild(label);
  footer.appendChild(track);
  overlay.appendChild(footer);
  document.body.appendChild(overlay);

  // ── Canvas context ──────────────────────────────────────────────────────────
  const ctx = canvas.getContext('2d')!;

  function resizeCanvas(): void {
    canvas.width  = canvas.offsetWidth  * devicePixelRatio;
    canvas.height = canvas.offsetHeight * devicePixelRatio;
  }
  window.addEventListener('resize', resizeCanvas);
  resizeCanvas();

  // ── Animation loop ──────────────────────────────────────────────────────────
  let startTime: number | null = null;
  let lastTime:  number | null = null;
  let rotY = 0;
  let rotX = 0;
  let rotZ = 0;
  let phase: 'forming' | 'holding' | 'fading' | 'done' = 'forming';
  let phaseStart = 0;

  function tick(now: number): void {
    if (startTime === null) { startTime = now; lastTime = now; resizeCanvas(); }
    const dt = (now - lastTime!) / 1000;
    lastTime = now;
    rotY += rotSpeedY * dt;
    rotX += rotSpeedX * dt;
    rotZ += rotSpeedZ * dt;

    const elapsed  = now - startTime;
    const progress = Math.min(elapsed / FORM_MS, 1);
    const eased    = easeInOut(progress);
    const pct      = Math.round(eased * 100);

    // Update progress UI
    fill.style.width  = `${eased * 100}%`;
    label.textContent = `LOADING ${pct}%`;
    label.style.left  = `${eased * 100}%`;

    // Phase transitions
    if (phase === 'forming' && progress >= 1) { phase = 'holding'; phaseStart = now; }
    if (phase === 'holding' && now - phaseStart >= HOLD_MS) {
      phase = 'fading'; phaseStart = now;
      window.dispatchEvent(new CustomEvent('loader-fading', { detail: { rotSpeedY } }));
    }
    if (phase === 'fading') {
      const elapsed = now - phaseStart;
      const bgFp       = Math.min(elapsed / BG_FADE_MS, 1);
      const particleFp = Math.min(elapsed / PARTICLE_FADE_MS, 1);
      bgDiv.style.opacity     = String(1 - easeOut(bgFp));
      footer.style.opacity    = String(1 - easeOut(bgFp));
      canvas.style.opacity    = String(1 - easeOut(particleFp));
      if (particleFp >= 1) {
        (window as any).__loaderActive = false;
        overlay.style.pointerEvents = 'none';
        window.removeEventListener('resize', resizeCanvas);
        return;
      }
    }

    // ── Render cloud ──────────────────────────────────────────────────────────
    const W  = canvas.width;
    const H  = canvas.height;
    const cx = W / 2;
    const cy = H / 2 + H * V_BIAS;
    const scale = Math.min(W, H) * 0.30;

    ctx.clearRect(0, 0, W, H);

    const cosY = Math.cos(rotY);
    const sinY = Math.sin(rotY);
    const cosX = Math.cos(rotX + rotOffsetX);
    const sinX = Math.sin(rotX + rotOffsetX);
    const cosZ = Math.cos(rotZ + rotOffsetZ);
    const sinZ = Math.sin(rotZ + rotOffsetZ);
    const visibleCount = Math.floor(eased * COUNT);

    const pts: { sx: number; sy: number; r: number; alpha: number; depth: number }[] = [];

    for (let i = 0; i < visibleCount; i++) {
      const idx = appearOrder[i]!;
      const px  = positions[idx * 3]!;
      const py  = positions[idx * 3 + 1]!;
      const pz  = positions[idx * 3 + 2]!;

      // X-axis rotation first (matches Three.js Euler 'XYZ' order)
      // On mobile cosX=1/sinX=0, so this is a no-op
      const rx0 = px;
      const ry0 = py * cosX - pz * sinX;
      const rz0 = py * sinX + pz * cosX;
      // Y-axis rotation second
      const rx1 =  rx0 * cosY + rz0 * sinY;
      const ry1 = ry0;
      const rz1 = -rx0 * sinY + rz0 * cosY;
      // Z-axis rotation third (roll — cosZ=1/sinZ=0 when rotSpeedZ=0, so no-op)
      const rx =  rx1 * cosZ - ry1 * sinZ;
      const ry =  rx1 * sinZ + ry1 * cosZ;
      const rz = rz1;

      const z   = rz * scale + FOV;
      const sx  = cx + (rx * scale * FOV) / z;
      const sy  = cy - (ry * scale * FOV) / z;
      const r   = Math.max((PARTICLE_SIZE * FOV) / z, 0.4);

      const depth = (rz + CLOUD_R) / (2 * CLOUD_R);
      const alpha = 0.08 + depth * 0.72;

      pts.push({ sx, sy, r, alpha, depth });
    }

    pts.sort((a, b) => a.depth - b.depth);

    for (const { sx, sy, r, alpha } of pts) {
      ctx.beginPath();
      ctx.arc(sx, sy, r, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(255,255,255,${alpha.toFixed(3)})`;
      ctx.fill();
    }

    requestAnimationFrame(tick);
  }

  // ── Hero handshake — sync rotation to hero's first frame ─────────────────
  // hero-particles.js dispatches 'hero-ready' on its first rendered frame with
  // its current rotY/rotX. Snap our accumulated values to match so the
  // overlay and sphere are in phase at the handoff moment regardless of
  // how long each script took to initialise.
  window.addEventListener('hero-ready', (e) => {
    const detail = (e as CustomEvent<{ rotY: number; rotX: number }>).detail;
    rotY = detail.rotY;
    rotX = detail.rotX;
  }, { once: true });

  requestAnimationFrame(tick);

  // ── Replay helper — resets animation state without re-running init ───────
  function replay(): void {
    startTime   = null;
    lastTime    = null;
    // Do NOT reset rotY/rotX/rotZ — carry them forward so the loader
    // stays roughly in phase with the hero, which never stops running.
    phase       = 'forming';
    phaseStart  = 0;
    // Reset progress bar
    fill.style.width      = '0%';
    label.textContent     = 'LOADING 0%';
    label.style.left      = '0%';
    // Reset layer opacities
    bgDiv.style.opacity   = '1';
    canvas.style.opacity  = '1';
    footer.style.opacity  = '1';
    overlay.style.pointerEvents = '';
    requestAnimationFrame(tick);
  }

  // ── Debug panel — only rendered when ?debug=1 ────────────────────────────
  if (new URLSearchParams(window.location.search).get('debug') === '1') {
    const panel = document.createElement('div');
    panel.style.cssText = [
      'position:fixed', 'bottom:24px', 'right:24px', 'z-index:20000',
      'background:rgba(0,0,0,0.75)', 'border:1px solid rgba(255,255,255,0.15)',
      'border-radius:8px', 'padding:14px 16px', 'min-width:220px',
      'font-family:"Public Sans",system-ui,sans-serif', 'font-size:11px',
      'font-weight:300', 'color:#fcfcfc', 'letter-spacing:0.08em',
      'pointer-events:all',
    ].join(';');

    function makeSlider(
      label: string,
      min: number, max: number, step: number,
      initial: number,
      onChange: (v: number) => void,
    ): HTMLElement {
      const row = document.createElement('div');
      row.style.cssText = 'margin-bottom:12px;';
      const header = document.createElement('div');
      header.style.cssText = 'display:flex;justify-content:space-between;margin-bottom:5px;';
      const lbl = document.createElement('span');
      lbl.textContent = label;
      lbl.style.opacity = '0.6';
      const val = document.createElement('span');
      val.textContent = initial.toFixed(3);
      header.appendChild(lbl);
      header.appendChild(val);
      const input = document.createElement('input');
      input.type = 'range';
      input.min = String(min);
      input.max = String(max);
      input.step = String(step);
      input.value = String(initial);
      input.style.cssText = 'width:100%;accent-color:#fcfcfc;cursor:pointer;';
      input.addEventListener('input', () => {
        const n = parseFloat(input.value);
        val.textContent = n.toFixed(3);
        onChange(n);
      });
      row.appendChild(header);
      row.appendChild(input);
      return row;
    }

    const title = document.createElement('div');
    title.textContent = 'LOADER DEBUG';
    title.style.cssText = 'font-size:9px;letter-spacing:0.18em;opacity:0.4;margin-bottom:14px;text-transform:uppercase;';
    panel.appendChild(title);

    // Direction toggle for Y spin
    const dirRow = document.createElement('div');
    dirRow.style.cssText = 'display:flex;align-items:center;justify-content:space-between;margin-bottom:12px;';
    const dirLabel = document.createElement('span');
    dirLabel.textContent = 'Y Direction';
    dirLabel.style.opacity = '0.6';
    const dirBtn = document.createElement('button');
    dirBtn.textContent = rotSpeedY >= 0 ? '→ Right' : '← Left';
    dirBtn.style.cssText = [
      'padding:4px 10px', 'background:rgba(255,255,255,0.08)',
      'border:1px solid rgba(255,255,255,0.2)', 'border-radius:4px',
      'color:#fcfcfc', 'cursor:pointer', 'font-family:inherit',
      'font-size:11px', 'letter-spacing:0.08em',
    ].join(';');
    dirBtn.addEventListener('click', () => {
      rotSpeedY = -rotSpeedY;
      dirBtn.textContent = rotSpeedY >= 0 ? '→ Right' : '← Left';
    });
    dirRow.appendChild(dirLabel);
    dirRow.appendChild(dirBtn);
    panel.appendChild(dirRow);

    panel.appendChild(makeSlider('Speed Y',   -0.6,      0.6,      0.005, rotSpeedY,  v => { rotSpeedY  = v; }));
    panel.appendChild(makeSlider('Speed X',   -0.4,      0.4,      0.005, rotSpeedX,  v => { rotSpeedX  = v; }));
    panel.appendChild(makeSlider('Speed Z',   -0.4,      0.4,      0.005, rotSpeedZ,  v => { rotSpeedZ  = v; }));
    panel.appendChild(makeSlider('Offset X',  -Math.PI,  Math.PI,  0.01,  rotOffsetX, v => { rotOffsetX = v; }));
    panel.appendChild(makeSlider('Offset Z',  -Math.PI,  Math.PI,  0.01,  rotOffsetZ, v => { rotOffsetZ = v; }));

    const replayBtn = document.createElement('button');
    replayBtn.textContent = '↺  Replay';
    replayBtn.style.cssText = [
      'width:100%', 'margin-top:4px', 'padding:7px 0',
      'background:rgba(255,255,255,0.08)', 'border:1px solid rgba(255,255,255,0.2)',
      'border-radius:5px', 'color:#fcfcfc', 'cursor:pointer',
      'font-family:inherit', 'font-size:11px', 'letter-spacing:0.1em',
    ].join(';');
    replayBtn.addEventListener('click', replay);
    panel.appendChild(replayBtn);

    document.body.appendChild(panel);
  }
}

// ── Dark cover — injected immediately (before DOMContentLoaded) so the hero
//    section is never visible before the loader overlay appears.
const earlyBg = document.createElement('div');
earlyBg.style.cssText = 'position:fixed;inset:0;z-index:10000;background:#080808;pointer-events:none;';
document.body.appendChild(earlyBg);

// ── DOMContentLoaded guard (matches nda-particles.ts pattern) ────────────────
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => { earlyBg.remove(); init(); });
} else {
  earlyBg.remove();
  init();
}
